import React, { useState, useEffect, useRef } from "react";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import axios from "axios";

export default function Clients() {
    const toast = useRef(null);

    const [clients, setClients] = useState([]);
    const [visible, setVisible] = useState(false);
    const [editingClient, setEditingClient] = useState(null);
    const [globalFilter, setGlobalFilter] = useState("");

    const [formData, setFormData] = useState({
        id: null,
        clientName: "",
        numberOfBranches: "",
        branchPrefix: "",
        status: "",
        dayendState: "",
        shortUrl: "",
    });

    const [errors, setErrors] = useState({});

    const statusOptions = [
        { label: "Active", value: "Active" },
        { label: "Inactive", value: "InActive" },
    ];

    // Fetch all clients
    const fetchClients = async () => {
        try {
            const res = await axios.get('https://treatway.co.in/Counter-Backend/api/server_masters/list/client_creation');
            setClients(res.data || []);
        } catch (err) {
            console.error(err);
            toast.current.show({ severity: "error", summary: "Error", detail: "Failed to load clients" });
        }
    };

    useEffect(() => {
        fetchClients();
    }, []);

    const onInputChange = (e, name) => {
        setFormData({ ...formData, [name]: e.target.value });
        if (errors[name])
            setErrors((prev) => {
                const n = { ...prev };
                delete n[name];
                return n;
            });
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.clientName.trim()) newErrors.clientName = 'Client Name is required';

        if (!formData.numberOfBranches || !String(formData.numberOfBranches).trim()) newErrors.numberOfBranches = 'Number of Branches is required';
        else if (isNaN(formData.numberOfBranches)) newErrors.numberOfBranches = 'Number of Branches must be a valid number';

        if (!formData.branchPrefix.trim()) newErrors.branchPrefix = 'Branch Prefix is required';

        if (!formData.status) newErrors.status = 'Status is required';

        if (formData.dayendState && isNaN(formData.dayendState)) newErrors.dayendState = 'Dayend State must be a number';

        if (formData.shortUrl && !/^https?:\/\/[^\s/$.?#].[^\s]*$/i.test(formData.shortUrl)) newErrors.shortUrl = 'Please enter a valid URL';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Save or Update Client
    const saveClient = async () => {
        if (!validateForm()) {
            toast.current.show({ severity: "error", summary: "Validation Error", detail: "Please fix errors before saving" });
            return;
        }

        try {
            const url = editingClient ? 'https://treatway.co.in/Counter-Backend/api/server_masters/save/client_creation' : 'https://treatway.co.in/Counter-Backend/api/server_masters/save/client_creation';

            await axios.post(url, JSON.stringify(formData), {
                headers: { "Content-Type": "application/json" },
            });

            toast.current.show({
                severity: "success",
                summary: "Success",
                detail: editingClient ? "Client Updated" : "Client Added"
            });

            setVisible(false);
            setEditingClient(null);
            setFormData({ id: null, clientName: "", numberOfBranches: "", branchPrefix: "", status: "", dayendState: "", shortUrl: "" });
            fetchClients(); // refresh table
        } catch (err) {
            console.error(err);
            toast.current.show({ severity: "error", summary: "Error", detail: "Something went wrong" });
        }
    };

    // Open dialog for edit
    const editClient = async (rowData) => {
        try {
            const res = await axios.get(`https://treatway.co.in/Counter-Backend/api/server_masters/get_client_by_id/${rowData.client_id}`);

            const data = res.data;

            setEditingClient(true);
            setFormData({
                id: data.client_id,
                clientName: data.client_name,
                numberOfBranches: data.no_of_branches,
                branchPrefix: data.branch_prefix,
                status: data.status,
                dayendState: data.dayend_mailpdf,
                shortUrl: data.short_url,
            });
            setVisible(true);
        } catch (err) {
            console.error(err);
            toast.current.show({ severity: "error", summary: "Error", detail: "Failed to load client details" });
        }
    };

    // Dialog footer
    const dialogFooter = (
        <div>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setVisible(false)} />
            <Button label="Save" icon="pi pi-check" onClick={saveClient} />
        </div>
    );

    return (
        <>
            <Toast ref={toast} />
            <h2 className="text-xl font-bold mb-4">Clients</h2>
            <div className="card">
                <div className="flex flex-wrap justify-between items-center mb-4 gap-3">
                    <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                        <Button
                            label="Add Client"
                            icon="pi pi-plus"
                            onClick={() => {
                                setVisible(true);
                                setEditingClient(null);
                                setFormData({ id: null, clientName: '', numberOfBranches: '', branchPrefix: '', status: '', dayendState: '', shortUrl: '' });
                                setErrors({});
                            }}
                        />
                    </div>
                    <div className="flex items-center ml-auto min-w-[250px] justify-end w-full sm:w-auto">
                        <span className="p-input-icon-left w-full">
                            <i className="pi pi-search"></i>
                            <InputText value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)} placeholder="Search..." className="p-inputtext-sm w-full" />
                        </span>
                    </div>
                </div>

                <DataTable
                    value={clients}
                    showGridlines
                    paginator
                    rows={25}
                    rowsPerPageOptions={[10, 25, 50]}
                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                    tableStyle={{ minWidth: '50rem' }}
                    className="p-datatable-sm"
                    emptyMessage="No clients found."
                    globalFilter={globalFilter}
                >
                    <Column field="client_name" header="Client Name" />
                    <Column field="no_of_branches" header="Branches" />
                    <Column field="status" header="Status" />
                    <Column field="short_url" header="Short URL" />
                    <Column header="Actions" body={(rowData) => <Button icon="pi pi-pencil" className="p-button-rounded p-button-warning mr-2" onClick={() => editClient(rowData)} />} />
                </DataTable>

                <Dialog
                    header={editingClient ? 'Edit Client' : 'Add Client'}
                    visible={visible}
                    style={{ width: '420px' }}
                    footer={dialogFooter}
                    onHide={() => {
                        setVisible(false);
                        setErrors({});
                    }}
                    modal
                    draggable={false}
                    resizable={false}
                >
                    <div className="field">
                        <label htmlFor="clientName">
                            Client Name <span style={{ color: 'red' }}>*</span>
                        </label>
                        <InputText id="clientName" value={formData.clientName} onChange={(e) => onInputChange(e, 'clientName')} placeholder="Enter client name" className={`w-full ${errors.clientName ? 'p-invalid' : ''}`} />
                        {errors.clientName && <small className="p-error">{errors.clientName}</small>}
                    </div>

                    <div className="field">
                        <label htmlFor="numberOfBranches">
                            Number of Branches <span style={{ color: 'red' }}>*</span>
                        </label>
                        <InputText
                            id="numberOfBranches"
                            value={formData.numberOfBranches}
                            onChange={(e) => onInputChange(e, 'numberOfBranches')}
                            placeholder="Enter number of branches"
                            keyfilter="int"
                            className={`w-full ${errors.numberOfBranches ? 'p-invalid' : ''}`}
                        />
                        {errors.numberOfBranches && <small className="p-error">{errors.numberOfBranches}</small>}
                    </div>

                    <div className="field">
                        <label htmlFor="branchPrefix">
                            Branch Prefix <span style={{ color: 'red' }}>*</span>
                        </label>
                        <InputText id="branchPrefix" value={formData.branchPrefix} onChange={(e) => onInputChange(e, 'branchPrefix')} placeholder="Enter branch prefix" className={`w-full ${errors.branchPrefix ? 'p-invalid' : ''}`} />
                        {errors.branchPrefix && <small className="p-error">{errors.branchPrefix}</small>}
                    </div>

                    <div className="field">
                        <label htmlFor="status">
                            Status <span style={{ color: 'red' }}>*</span>
                        </label>
                        <Dropdown
                            id="status"
                            value={formData.status}
                            options={statusOptions}
                            onChange={(e) => {
                                setFormData({ ...formData, status: e.value });
                                if (errors.status)
                                    setErrors((prev) => {
                                        const n = { ...prev };
                                        delete n.status;
                                        return n;
                                    });
                            }}
                            placeholder="Select Status"
                            className={`w-full ${errors.status ? 'p-invalid' : ''}`}
                        />
                        {errors.status && <small className="p-error">{errors.status}</small>}
                    </div>

                    <div className="field">
                        <label htmlFor="dayendState">Dayend State</label>
                        <InputText id="dayendState" value={formData.dayendState} onChange={(e) => onInputChange(e, 'dayendState')} placeholder="Enter dayend state (optional)" className={`w-full ${errors.dayendState ? 'p-invalid' : ''}`} />
                        {errors.dayendState && <small className="p-error">{errors.dayendState}</small>}
                    </div>

                    <div className="field">
                        <label htmlFor="shortUrl">Short URL</label>
                        <InputText id="shortUrl" value={formData.shortUrl} onChange={(e) => onInputChange(e, 'shortUrl')} placeholder="https://example.com (optional)" className={`w-full ${errors.shortUrl ? 'p-invalid' : ''}`} />
                        {errors.shortUrl && <small className="p-error">{errors.shortUrl}</small>}
                    </div>
                </Dialog>
            </div>
        </>
    );
}
